import axios from 'axios';
import {
  getAccessToken, getRefreshToken, setTokens, clearTokens,
} from './tokens';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 60_000,
});

// Logout callback registered by AuthContext so we can clear React state too.
let _forceLogout = null;
export const setForceLogout = (fn) => { _forceLogout = fn; };

function doLogout() {
  clearTokens();
  if (_forceLogout) {
    _forceLogout();
  } else {
    // Fallback before AuthContext registers the callback
    window.location.href = '/portal/login';
  }
}

// 404 on /users/:id means admin deleted this user → treat as gone.
// 401 is handled separately by the refresh flow below.
function isAccountGoneError(status, url) {
  if (status === 404 && /\/users\/[^/]+$/.test(url)) return true;
  return false;
}

// ── Request: attach access token ────────────────────────────────────────────
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response: refresh on 401, retry once ────────────────────────────────────
//
// Single in-flight refresh promise so a burst of concurrent 401s only triggers
// one /auth/refresh call. All callers wait on the same promise and then retry.
let refreshPromise = null;

async function performRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token');

  // Use a bare axios call (NOT `api`) so the interceptor doesn't recurse.
  const res = await axios.post(
    `${process.env.REACT_APP_API_URL}/auth/refresh`,
    { refreshToken },
    { timeout: 30_000 },
  );
  const next = {
    accessToken: res.data.accessToken || res.data.token,
    refreshToken: res.data.refreshToken,
  };
  if (!next.accessToken) throw new Error('Refresh response missing accessToken');
  setTokens(next);
  return next.accessToken;
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status   = err.response?.status;
    const original = err.config || {};
    const url      = original.url || '';

    // Hard "account is gone" → straight logout, no refresh attempt.
    if (isAccountGoneError(status, url)) {
      doLogout();
      return Promise.reject(err);
    }

    // Only try to refresh on 401, and only once per original request.
    // Don't try to refresh the refresh/login endpoints themselves (would loop).
    const isAuthEndpoint =
      /\/auth\/(refresh|login)$/.test(url);
    if (status !== 401 || original._retried || isAuthEndpoint) {
      // No refresh token → logout (any 401 is fatal at that point).
      if (status === 401 && !getRefreshToken()) doLogout();
      return Promise.reject(err);
    }

    original._retried = true;

    try {
      if (!refreshPromise) {
        refreshPromise = performRefresh().finally(() => { refreshPromise = null; });
      }
      const newToken = await refreshPromise;
      // Replay the original request with the new token.
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${newToken}`;
      return api.request(original);
    } catch (refreshErr) {
      // Refresh itself failed → session is truly over.
      doLogout();
      return Promise.reject(refreshErr);
    }
  },
);

export default api;
